import { Box, Flex, Grid, Text, useT } from '@minetrace/ui'
import { LuCircleCheck, LuCircleHelp, LuFilePlus2, LuShieldAlert } from 'react-icons/lu'
import {
  INDICE_RESERIALIZACION_NO_APLICA,
  type IndiceVerificacionResponse,
} from '../../../api/models/response/indiceDigital'
import { themeColors } from '../../../theme'
import { describeVerificacion, type VerificacionTone } from './verificacion'
import { formatDateTime } from '../../../lib/dates'

const TONE_ICON: Record<VerificacionTone, React.ReactNode> = {
  ok: <LuCircleCheck size={16} />,
  neutral: <LuCircleHelp size={16} />,
  alarm: <LuShieldAlert size={16} />,
}

/**
 * El veredicto de una versión sellada, pintado por `severidad` y sólo por ella
 * (ver `verificacion.ts`).
 *
 * EL PARTE DE NOVEDADES NO ES UNA ADVERTENCIA. Cuando el expediente ha crecido desde
 * el sello, `cambios` trae los recuentos y se enseñan en el MISMO tono que el caso
 * íntegro, con un icono de "documento añadido" y no de alerta. Es un expediente de
 * sólo añadido: crecer es lo que hace cuando funciona.
 */
export function VerificacionPanel({ verificacion }: { verificacion: IndiceVerificacionResponse }) {
  const { t } = useT()
  const visual = describeVerificacion(verificacion)
  const cambios = verificacion.cambios

  return (
    <Box borderRadius="lg" bg={visual.bg} p={4}>
      <Flex alignItems="center" gap={2} color={visual.color}>
        {TONE_ICON[visual.tone]}
        <Text fontSize="sm" fontWeight="700">{t(visual.labelKey)}</Text>
      </Flex>

      {visual.hintKey && (
        <Text fontSize="xs" color={themeColors.textSecondary} mt={2} lineHeight="1.6">
          {t(visual.hintKey)}
        </Text>
      )}

      {/* `detalle` viene redactado por el backend para enseñarse tal cual y es nulo
          en los dos estados sanos. */}
      {verificacion.detalle && (
        <Text fontSize="xs" color={themeColors.textSecondary} mt={2} lineHeight="1.6">
          {verificacion.detalle}
        </Text>
      )}

      {cambios && (
        <Box mt={4} pt={3} borderTop="1px solid" borderColor="rgba(0,0,0,0.06)">
          <Flex alignItems="center" gap={2} mb={3} color={themeColors.textSecondary}>
            <LuFilePlus2 size={14} />
            <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.05em">
              {t('expedientes.electronico.verify.news')}
            </Text>
          </Flex>
          <Grid templateColumns="repeat(auto-fit, minmax(130px, 1fr))" gap={3}>
            <Metric value={`+${cambios.documentosNuevos}`} label={t('expedientes.electronico.verify.newDocuments')} />
            <Metric value={`+${cambios.foliosNuevos}`} label={t('expedientes.electronico.verify.newFolios')} />
            <Metric value={String(cambios.documentosSellados)} label={t('expedientes.electronico.verify.sealedDocuments')} />
            <Metric value={String(cambios.documentosAhora)} label={t('expedientes.electronico.verify.nowDocuments')} />
            <Metric value={String(cambios.foliosSellados)} label={t('expedientes.electronico.verify.sealedFolios')} />
            <Metric value={String(cambios.foliosAhora)} label={t('expedientes.electronico.verify.nowFolios')} />
          </Grid>
          {cambios.ultimaIncorporacion && (
            <Text fontSize="11px" color={themeColors.textMuted} mt={3}>
              {t('expedientes.electronico.verify.lastIncorporation')}: {formatDateTime(cambios.ultimaIncorporacion)}
            </Text>
          )}
        </Box>
      )}

      <Flex gap={4} mt={4} flexWrap="wrap">
        <Text fontSize="10px" color={themeColors.textMuted}>
          {t('expedientes.electronico.verify.scope')}: {verificacion.alcanceName}
        </Text>
        {verificacion.reserializacion !== INDICE_RESERIALIZACION_NO_APLICA && (
          <Text fontSize="10px" color={themeColors.textMuted}>
            {t('expedientes.electronico.verify.reserialization')}: {verificacion.reserializacionName}
            {' — '}
            {t('expedientes.electronico.verify.reserializationHint')}
          </Text>
        )}
      </Flex>
    </Box>
  )
}

function Metric({ value, label }: { value: string; label: string }) {
  return (
    <Box>
      <Text fontSize="md" fontWeight="700" color={themeColors.text} lineHeight="1.2">{value}</Text>
      <Text fontSize="10px" color={themeColors.textMuted}>{label}</Text>
    </Box>
  )
}
